import { Injectable } from "@angular/core";
import { UnitControlRefreshModel } from "../../../shared/models/program-control-related/UnitControlRefreshModel";
import { BehaviorSubject } from "rxjs";
import { ControlStateUpdateModel } from "../../../shared/models/program-control-related/ControlStateUpdateModel";
import { NotificationHandlerService } from "../../../index";
import { UnitProgramControlService } from "../unit-program-control.service";
import { Observable } from "rxjs/Observable";
import { UnitControlWithStateModel } from "../../../shared/models/program-control-related/UnitControlWithStateModel";

const REFRESH_TIMEOUT_FULL = 60000;
const REFRESH_SOURCE_TIMER = 'timer';

@Injectable()
export class ControlStateRefreshService {

    private controlsWaitingRefresh: UnitControlRefreshModel[];
    private controlRefreshed = new BehaviorSubject<ControlStateUpdateModel>(null);

    constructor(private _upcService: UnitProgramControlService, private toast: NotificationHandlerService) {
        this.controlsWaitingRefresh = [];
    }

    // due to timeouts being paused on app pause
    // loop through all controls waiting refresh, rebuild timeouts based on time passed
    private recheckControlTimeouts(): void {
        // loop through all controls waiting refresh
        for (let i = 0; i < this.controlsWaitingRefresh.length; i++) {
            // get current control, current time in milliseconds and calc time remaining
            let currentControl = this.controlsWaitingRefresh[i];
            let time = new Date().getTime();
            let timeRemaining = currentControl.TimeStamp - time;
            // if there is still time remaining
            if (timeRemaining > 0) {
                // clear the timeout then..
                clearTimeout(currentControl.TimeoutId);
                // start another timeout with the new time interval
                this.startStateRefreshTimer(i, currentControl, timeRemaining);
                // else the time must have passed
            } else {
                // clear the timeout incase it hasent already been done
                clearTimeout(currentControl.TimeoutId);
                // then refresh the control
                this.refreshControl(i);
            }
        }
    }

    public areControlsRefreshing(): boolean {
        return this.controlsWaitingRefresh.length > 0;
    }

    // add a control to be refreshed using timer
    public addRefreshControl(control: UnitControlWithStateModel): Observable<ControlStateUpdateModel> {
        //console.log('Adding control for refresh: ' + control.UnitControlId);
        let refreshModel: UnitControlRefreshModel = { Control: control, TimeoutId: 0, TimeStamp: new Date().getTime() + REFRESH_TIMEOUT_FULL };
        this.controlsWaitingRefresh.push(refreshModel);
        let ctrlIndex = this.controlsWaitingRefresh.findIndex(c => c.Control.UnitControlId == control.UnitControlId);
        if (ctrlIndex == -1) {
            this.controlsWaitingRefresh.push(refreshModel);
        }
        this.startStateRefreshTimer(ctrlIndex, refreshModel);
        return this.controlRefreshed.asObservable();
    }

    // remove a control awaiting refresh by timer -- interrupted by push data update
    public removeRefreshControl(controlId: number): boolean {
        //console.log('Removing control from refresh: ' + controlId);
        let ctrlRefresh = this.controlsWaitingRefresh.filter(c => c.Control.UnitControlId == controlId);
        ctrlRefresh.forEach((c) => {
            clearTimeout(c.TimeoutId);
        });
        this.controlsWaitingRefresh = this.controlsWaitingRefresh.filter(c => c.Control.UnitControlId != controlId);
        //console.log('Removeing refresh controls, count:' + ctrlRefresh.length);
        return ctrlRefresh && ctrlRefresh.length > 0;
    }

    // check to see if a control is refreshing using its id
    public isControlRefreshing(control: UnitControlWithStateModel): boolean {
        let refreshingControl = this.controlsWaitingRefresh.find(c => c.Control.UnitControlId == control.UnitControlId);
        if (refreshingControl) {
            // if(new Date().getMilliseconds() >= this.controlsWaitingRefresh[controlIndex].timeStamp.getMilliseconds() + REFRESH_TIMEOUT_FULL) {
            //   this.refreshControl(controlIndex);
            // }
            // if there is a control waiting refresh but its state is not the same as the control asking if its being refreshed
            if (refreshingControl.Control.CurrentState != control.CurrentState) {
                // clear the timeout && remove from refresh
                clearTimeout(refreshingControl.TimeoutId);
                this.controlsWaitingRefresh = this.controlsWaitingRefresh.filter(c => c.Control.UnitControlId != control.UnitControlId);
                return false;
            } else {
                // else its refresh is still valid return true
                return true;
            }
        }
        else {
            return false;
        }
    }

    // get the observable to subscribe to the refresh events
    public getRefreshSub(): Observable<ControlStateUpdateModel> {
        return this.controlRefreshed.asObservable();;
    }

    // kick starts the timer for a control timer refresh
    private startStateRefreshTimer(controlIndex: number, refreshModel: UnitControlRefreshModel, refreshTime: number = null): void {
        let time = refreshTime != null ? refreshTime : REFRESH_TIMEOUT_FULL;
        //console.log('STARTING Control Refresh Timer, Timeout ==> ' + time);
        setTimeout(() => {
            //console.log('Refresh timer triggered: ' + refreshModel.Control.UnitControlId);
            this.refreshControl(controlIndex);
        }, time);
    }

    // refresh a control using its id, called when when the timer elapses for the refresh
    private refreshControl(ctrlIndex: number): void {
        let ctrl = this.controlsWaitingRefresh[ctrlIndex];
        //console.log('Refreshing control: ' + ctrl.Control.UnitControlId);
        if (ctrl) {
            this._upcService.getControlWithState(ctrl.Control.UnitControlId)
                .subscribe((res: any) => {
                    if (res.Result == 'Success') {
                        // success result
                        this.handleControlRefreshed(ctrl, res.Data);
                    } else {
                        // handle error
                    }
                }, (error: any) => {
                    // handle error
                });
        }
    }

    // handle a successful timer refresh of a control
    private handleControlRefreshed(ctrl: UnitControlRefreshModel, updatedControl: UnitControlWithStateModel): void {
        //console.log('Control successfully refreshed: ' + ctrl.Control.UnitControlId);
        this.controlRefreshed.next({ source: REFRESH_SOURCE_TIMER, controlId: updatedControl.UnitControlId, state: updatedControl.CurrentState });
        this.controlRefreshed.next(null);
        this.controlsWaitingRefresh = this.controlsWaitingRefresh.filter(c => c.Control.UnitControlId != ctrl.Control.UnitControlId);

        //console.log(ctrl)
        //console.log(updatedControl)

        //let site = ctrl.Control.SiteName && ctrl.Control.SiteName != '' ? ctrl.Control.SiteName : 'Control Update';
        if (ctrl.Control.CurrentState != updatedControl.CurrentState) {
            let message = ctrl.Control.ControlName + ' is now ' + updatedControl.CurrentState + '.';
            this.toast.handleSuccess(message);
        }
    }

}


// WEBPACK FOOTER //
// ./angularApp/app/units/units-program-control/unit-controls/control-state-refresh.service.ts